<template>
  <div>
    <div class="flex-container space-left">
      <div class="fw-bold min-width">{{$t('Webportal.AggregationLevel') + ' 1:'}}</div>
      <treeselect instance-id="propertyAggregationTreeselect"
                  ref="propertyAggregationTreeselect"
                  v-model="aggregationValue"
                  :clearable="false"
                  :deleteRemoves="false"
                  :backspaceRemoves="false"
                  :auto-load-root-options="false"
                  :placeholder="$t('Webportal.PropertyFilter.Placeholder')"
                  :options="aggregationItems"
                  :disableFuzzyMatching="true"
                  :multiple="false"
                  :showCount="true"
                  showCountOf="LEAF_DESCENDANTS"
                  :defaultExpandLevel="1"
                  :disable-branch-nodes="true"
                  style="max-width: 20em; margin-left: 10px;align-content: start;"
                  @input="handleTreeselectInput"
                  @close="aggLevelchanged"/>
      <div>
        <b-button variant="primary"
                  :disabled="!aggregationValue2"
                  @click="changeAggLevel()"
                  size="sm"
                  style="margin-left: 10px;">
          <font-awesome-icon icon="exchange-alt" />
        </b-button>
      </div>
      <div class="fw-bold" style="margin-left: 10px;">2:</div>
      <treeselect instance-id="propertyAggregationTreeselect2"
                  ref="propertyAggregationTreeselect2"
                  v-model="aggregationValue2"
                  :clearable="true"
                  :deleteRemoves="false"
                  :backspaceRemoves="false"
                  :auto-load-root-options="false"
                  :placeholder="$t('Webportal.PropertyFilter.Placeholder')"
                  :options="aggregationItems"
                  :disableFuzzyMatching="true"
                  :multiple="false"
                  :showCount="true"
                  showCountOf="LEAF_DESCENDANTS"
                  :defaultExpandLevel="1"
                  :disable-branch-nodes="true"
                  style="max-width: 20em; margin-left: 10px;align-content: start;"
                  :disabled="!aggregationValue"
                  @input="handleTreeselectInput"
                  @close="aggLevelchanged"/>
    </div>
    <div class="flex-container space-left space-top">
      <div class="fw-bold min-width">{{$t('Webportal.TopResultCount')}}:</div>
      <b-form-select class="col-xl-2 col-lg-2 col-md-3 col-sm-4" v-model="topResultCount" style="margin-left: 10px;" @change="onTopResultCountChange">
        <option :value="null">{{ $t('Webportal.Unlimited') }}</option>
        <option :value="10">10</option>
        <option :value="25">25</option>
        <option :value="100">100</option>
      </b-form-select>
    </div>
    <div class="flex-container space-left space-top">
      <div class="fw-bold min-width">{{ $t('Webportal.Kpis') }}:</div>
      <b-form-group class="col-xl-8 col-lg-8 col-md-8 col-sm-8" style="margin-left: -5px;">
        <treeselect ref="kpiTreeselect"
                    v-model="kpiValue"
                    :auto-load-root-options="false"
                    :placeholder="$t('Webportal.KpiFilter.Placeholder')"
                    :options="kpiItems"
                    valueFormat="object"
                    :disabled="isCluster"
                    :disableFuzzyMatching="true"
                    :multiple="true"
                    :showCount="true"
                    showCountOf="LEAF_DESCENDANTS"
                    :defaultExpandLevel="1"
                    @input="handleKpiTreeselectInput"
                    @close="handleKpiTreeselectClose">
        </treeselect>
      </b-form-group>
    </div>

    <div class="dx-fieldset" v-if="gridData">
      <dx-data-grid
        id="dxdatagrid"
        ref="dxdatagrid"
        key-expr="AggLevelValue"
        :data-source="gridData"
        :show-borders="true"
        :word-wrap-enabled="true"
        :show-column-lines="false"
        :show-row-lines="true"
        :focused-row-enabled="false"
        :onCellPrepared="onCellPrepared"
        :allow-column-reordering="false"
        :row-alternation-enabled="false"
        :column-hiding-enabled="true"
        :onRowClick="onRowClick"
        :key="componentKey"
        :onRowCollapsed="onGridRowCollapsed"
        :onRowExpanded="onGridRowExpanded"
        :onAdaptiveDetailRowPreparing="onGridAdaptiveDetailRowPreparing"
        :columnAutoWidth="true"
        @toolbar-preparing="onToolbarPreparing($event)">
        <dx-grouping :autoExpandAll="false">
          <dx-texts
            :groupContinuedMessage="$t('WebReport.DataGridContinuedMessage')"
            :groupContinuesMessage="$t('WebReport.DataGridContinuesMessage')"
          />
        </dx-grouping>

        <dx-paging :enabled="false"/>
        <dx-sorting mode="none"/>

        <dx-load-panel :enabled="true"/>
        <dx-scrolling mode="virtual"/>

        <dx-export :enabled="false"/>

        <template #summaryCellTemplate="{ data }">
          <div>{{  data  }}</div>
          <!-- <summary-cell :cell-data="data"/> -->
        </template>

        <dx-column  data-field="GroupValue"
                    :group-index="0"
                    cell-template="summaryCellTemplate"
                    :sortingMethod="groupSort" />

        <dx-column :caption="$t('Webportal.Ranking')">
          <dx-column alignment="left" data-field="RankingOrder" :caption="$t('Webportal.Dashboard.Order')" :width="100"/>
        </dx-column>

        <dx-column :caption="$t('Webportal.AggregationLevel')">
          <dx-column data-field="AggLevelValue" :caption="getSelectedAggLevelName()" :width="200"/>
        </dx-column>

        <!-- Dummy column so that all following columns are shown in group rows -->
        <dx-column name="spacer" :width="1" :allowExporting="false" />

        <dx-column :caption="$t('Webportal.Dashboard.Checks')">
          <dx-column alignment="left" data-field="SurveyInstanceCount" caption="N" cell-template="nTemplate" :width="100"/>
        </dx-column>

        <template v-for="(colObj, index) in getColumns()" :key="`${index}_colname`">
          <dx-column :caption="colObj.name" header-cell-template="topHeaderTemplate">
            <dx-column
              :data-field="getColumnDataName(index)"
              :visible="true"
              :caption="!colObj.hideCaption ? $t('Webportal.Result') : ''"
              :key="`${index}_result`"
              :hiding-priority="1000-index"
              cell-template="resultTemplate"
              alignment="left"
              :name="colObj.code"
            ></dx-column>
          </dx-column>
        </template>

        <dx-summary>
          <!-- Dummy column so that all following columns are shown in group rows -->
          <dx-group-item
            :show-in-group-footer="false"
            :align-by-column="true"
            column="spacer" />
        </dx-summary>

        <dx-group-panel :visible="false"/>
        <dx-search-panel :visible="true" :highlight-case-sensitive="true"/>

        <template #collapseall>
          <b-form-checkbox v-model="expandCollapseValue" size="sm"
                           :indeterminate="expandCollapseIndeterminate"
                           @change="onExpandCollapseAllChanged">
            {{ $t('Webportal.Dashboard.ExpandCollapse') }}
          </b-form-checkbox>
        </template>

        <template #download>
          <b-row>
            <div class="col-md-auto">
              <b-alert :show="noDataAlert"
                      dismissible
                      @dismissed="noDataAlert=0"
                      variant="warning">
                {{ $t('Webportal.NoDataFound') }}
              </b-alert>
              <b-button class="excelButton"
                        variant="primary"
                        :disabled="isExporting || !rankingdata"
                        @click="downloadRawData()"
                        size="sm"
                        v-b-tooltip.hover
                        :title="$t('Webportal.Download')">
                <font-awesome-icon icon="file-excel" />
              </b-button>
            </div>
            <div class="col-md-1" v-bind:class="{ inlineSpinner: isExporting }"></div>
          </b-row>
        </template>

        <template #resultTemplate="cellData">
          <result-cell  slot="resultTemplate"
                        :cell-data="cellData"
                        :thresholds="thresholds"
                        :percentageNumberOfDecimalPoints="percentageNumberOfDecimalPoints"
                        placeholderNaN=" " />
        </template>
        <template #nTemplate="data">
          <result-cell :cell-data="data" />
        </template>

        <template #topHeaderTemplate="data">
          <!-- NEU: Defensive Checks -->
          <div v-if="data && data.column && data.column.caption == 'Total'">
            Total
            <template v-if="rankingdata">
              <span v-if="rankingdata.MaxOverallCalculationType > 0"
                    v-b-tooltip.hover.nofade="toolTipDelay"
                    :title="$t('Webportal.AggregatedOverallCalculation.Average.Description')">
                - {{ $t('Webportal.AggregatedOverallCalculation.Average') }}
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
              <span v-else
                    v-b-tooltip.hover.nofade="toolTipDelay"
                    :title="$t('Webportal.AggregatedOverallCalculation.Standard.Description')">
                - {{ $t('Webportal.AggregatedOverallCalculation.Standard') }}
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </template>
          </div>
          <div v-else-if="data && data.column && data.column.caption">
            {{ data.column.caption }}
          </div>
          <!-- NEU: Fallback für undefined -->
          <div v-else>&nbsp;</div>
        </template>
      </dx-data-grid>
    </div>
  </div>
</template>

<script>
import {
  DxDataGrid,
  DxExport,
  DxColumn,
  DxGrouping,
  DxGroupPanel,
  DxPager,
  DxPaging,
  DxSearchPanel,
  DxTexts,
  DxSummary,
  DxGroupItem,
  DxValueFormat,
  DxSorting,
  DxScrolling,
  DxLoadPanel
} from "devextreme-vue/data-grid";

import ResultCell from "./ResultCell.vue";
import SummaryCell from "./SummaryCell.vue";
import EventBus from '@/services/EventBus.js';
import axios from 'axios'
import DashboardService from '@/services/DashboardService'
import AggregatedReportsService from '@/services/AggregatedReportsService';
import SurveysService from '@/services/SurveysService';
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import DownloadFileMixin from '@/mixins/DownloadFileMixin';
import ThresholdColor from '@/mixins/ThresholdColor-APP_TARGET';

export default {
  name: "RankingTable",

  components: {
    DxDataGrid,
    DxExport,
    DxColumn,
    DxGrouping,
    DxGroupPanel,
    DxPager,
    DxPaging,
    DxSearchPanel,
    DxTexts,
    DxSummary,
    DxGroupItem,
    DxValueFormat,
    DxSorting,
    DxScrolling,
    DxLoadPanel,
    ResultCell,
    SummaryCell
  },

  mixins: [ AppConfig, DownloadFileMixin, ThresholdColor ],

  props: {
    isCluster: { default: true },
    thresholds: null,
    percentageNumberOfDecimalPoints: { type: Number, default: 2 },
    filtervalues: null,
    selection: null
  },

  watch: {
    filtervalues: {
      // immediate: true,
      handler(newVal, oldVal) {
        if (!_.isEqual(newVal.clients, oldVal.clients))
        {
          this.rankingdata = null;
          this.aggregationValue = null;
          this.aggregationValue2 = null;
          if (!this.loadOptions()) {
            this.handleDataChanged();
          }
          this.componentKey +=1;
        }
      },
    },

    selection: {
      // immediate: true,
      handler(newVal, oldVal) {
        // console.log('RankingTable - watch - selection');
        if (!this.getSurveyKpis()) {
          this.loadData();
        }
      },
    },

    isCluster: {
      // immediate: true,
      handler(newVal, oldVal) {

        if (newVal === true) {
          this.kpiValue = [];
          this.lastKnownKpiIds = null;
        } else {
          this.kpiValue = this.kpiItems;
        }

        this.handleDataChanged();
        this.getSurveyKpis();
        this.componentKey +=1;
      },
    },
  },

  created() {
    this.loadOptions();
  },

  mounted() {
    EventBus.$on('LocalizationChanged', this.localizationChanged);
  },

  beforeUnmount() {
    EventBus.$off('LocalizationChanged', this.localizationChanged);
  },

  data() {
    return {
      componentKey: 0,
      expandCollapseValue: false,
      expandCollapseIndeterminate: false,
      rankingdata: null,
      gridData: null,
      aggregationItems: [],                 // Data items for aggregation treeselect
      aggregationValue: null,               // Selected aggregation item
      aggregationValue2: null,              // Selected aggregation item
      isLoading: false,
      cancelToken: null,
      isExporting: false,
      noDataAlert: 0,
      currentSummaryRowCounter: 0,
      topResultCount: null,
      summaryRows: [],
      intervalReference: undefined,
      aggLevelChangeInitiated: false,
      lastAggLevel1Value: null,
      lastAggLevel2Value: null,
      kpiItems: [],                         // Data items for kpi treeselect
      kpiValue: [],                         // Selected kpi items
      lastKnownKpiIds: null,                // remembers last known kpi selection (for selection change detection)
      lastKnownsSurveyIds: null,            // remembers last known survey selection (for selection change detection)
      summaryCellComponent: SummaryCell
    };
  },

  methods: {
    localizationChanged(localizationId) {
      this.aggregationValue = null;
      this.lastKnownsSurveyIds = null; // force reload for the translations
      this.gridData = null;
      this.loadOptions();
    },

    changeAggLevel() {
      if (this.aggregationValue && this.aggregationValue2) {

        this.aggLevelChangeInitiated = true;

        const aggLvl2 = this.aggregationValue2;
        this.aggregationValue2 = this.aggregationValue;
        this.aggregationValue = aggLvl2;
      }
    },

    groupSort(val1, val2)
    {
      // only sort Total to the top
      if (val1 === ' Total') {
        return -1;
      } else if (val2 === ' Total') {
        return 1;
      }

      return 0; // don't sort anything
    },

    loadOptions() {
      let subItems = null;
      this.aggregationItems = this.defaultAggregationItems();

      if (this.filtervalues && this.filtervalues.customProperties) {
        if (this.filtervalues.clients && this.filtervalues.clients.length > 0) {
          subItems = {
            id: -1,
            label: this.filtervalues.clients[0].label,
            children: []
          };
        } else {
          subItems = {
            id: -1,
            label: 'CustomProperties',
            children: []
          };
        }

        for (let item of this.filtervalues.customProperties) {
          const itemToPush = {
            id: `Property^${item.id}`,
            label: item.label
          };
          subItems.children.push(itemToPush);
        }

        this.aggregationItems.push(subItems);
      }

      // load KPIs for selected surveys
      return this.getSurveyKpis();
    },

    // Get survey structure data for the structure treeselect
    // it will return whether kpis are loaded from backend or not
    getSurveyKpis() {
      let retVal = false;

      if (!this.isCluster && this.selection && this.selection.surveys && this.selection.surveys.length > 0)
      {
        // console.log('getSurveyKpis');
        // console.log(this.lastKnownsSurveyIds);
        // console.log(this.selection.surveys);
        if (this.lastKnownsSurveyIds != this.selection.surveys) {
          retVal = true;
          SurveysService.getSurveyKpis({ surveyIds: [ this.selection.surveys ] })
            .then(response => {
              this.kpiItems = response.data;
              this.lastKnownsSurveyIds = this.selection.surveys;
              this.kpiValue = this.kpiItems;
              this.loadData();
            });
        }
      };

      return retVal;
    },

    handleTreeselectInput(value, instanceId) {

      // prevent double backend calls, if switch button is clicked
      if (this.aggLevelChangeInitiated) {
        this.aggLevelChangeInitiated = false;
        return;
      }

      const changed = instanceId === 'propertyAggregationTreeselect' ? value !== this.lastAggLevel1Value : value !== this.lastAggLevel2Value;

      if (changed) {

        instanceId === 'propertyAggregationTreeselect' ? this.lastAggLevel1Value = value : this.lastAggLevel2Value = value;
        this.loadData();
      }
    },

    handleKpiTreeselectInput(value) {
      if (value.length === 0) {
        this.handleKpiTreeselectClose(value);
      }
    },

    handleKpiTreeselectClose(value) {
        const compareList = this.getKpiItemList(this.kpiItems, value);
        if (compareList != this.lastKnownKpiIds) {
          this.loadData();
        }
    },

    getKpiItemList(allKpiArray, selectedKpiArray) {
        // Returns an array of all KPI ids recursively for the given node
        let returnKpiIds = (obj) => {
            return _.concat(obj.id && obj.id[0] === 'K' ? obj.id.substring(1) : [],
                obj.children ? _.flatMapDeep(obj.children, returnKpiIds) : []);
        };
        let kpiIds = !_.isEqual(selectedKpiArray, allKpiArray) ? _.flatMapDeep(selectedKpiArray, returnKpiIds) : null;
        return (kpiIds && kpiIds.length > 0) ? kpiIds.join(',') : null;
    },

    loadData() {

      if (this.aggregationValue) {

        this.isLoading = true;
        const options = this.getCurrentOptions();
        const token = this.initCancelToken(this.cancelToken);
        this.gridData = null;

        // add selected kpi items to selection filter
        this.selection.kpiIds = this.getKpiItemList(this.kpiItems, this.kpiValue);

        // console.log(this.selection);
        DashboardService.getDashboardRankingData(this.selection, options, token)
          .then(response => {
            this.isLoading = false;
            this.rankingdata = response.data;
            this.lastKnownKpiIds = this.selection.kpiIds;
            this.handleDataChanged();
          })
          .catch(error => {
            console.warn('Error to receive ranking data: ' + error);
            this.isLoading = false;
          });
      }
    },

    aggLevelchanged(value, instanceId) {

      if (value && value.length === 0) {
        this.handleTreeselectInput(value, instanceId);
      }
    },

    getCurrentOptions(forJson = true) {
      return {
        isRanking: true,
        aggregationLevel1: this.aggregationValue,
        aggregationLevel2: this.aggregationValue2,
        topResultCount: this.topResultCount,
        includeKpiPercentages: !this.isCluster,
        includeKpiPoints: false,
        includeClusterPercentages: this.isCluster,
        includeClusterPoints: false,
        includeOverallPoints: this.hasRawDataPointSelection(),
        colorizeResults: APP_TARGET !== 'db',
        useCompactLayout: true,
        propertyUseLogicalAnd: true
      };
    },

    getCurrentFilter() {
      return {
        surveyIds: this.selection.surveys ? this.selection.surveys.split(',') : null,
        waveIds: this.selection.waves ? this.selection.waves.split(',') : null,
        locations: this.selection.locations ? this.selection.locations.split(',') : null,
        propertyIds: this.selection.customProperties? this.selection.customProperties.split(','): null,
        kpiIds: null
      }
    },

    initCancelToken(token) {
      // console.log('Dashboard - initCancelToken');

      // Cancel previous request
      if (token)
        token.cancel();
      return axios.CancelToken.source();
    },

    getColumns() {
      let retVal = [];

      if (this.rankingdata) {

        // define the total column for row summary
        retVal.push({code: 'Total', name: 'Total'});

        if (this.isCluster) {
          if (this.rankingdata.Cluster) {
            for (let clu of this.rankingdata.Cluster) {
              if (clu.Code && clu.Code.length > 0) {
                retVal.push({
                  code: clu.Code,
                  name: (clu.Name && clu.Name.length > 0) ? clu.Name : clu.Code
                });
              }
            }
          }
        } else {
          if (this.rankingdata.KPI) {
            for (let kpi of this.rankingdata.KPI) {
              if (kpi.Code && kpi.Code.length > 0) {
                retVal.push({
                  code: kpi.Code,
                  name: (kpi.Name && kpi.Name.length > 0) ? kpi.Name : kpi.Code
                });
              } else if (kpi.GroupCode) {
                retVal.push({
                  code: kpi.GroupCode,
                  name: (kpi.GroupName && kpi.GroupName.length > 0) ? kpi.GroupName : kpi.GroupCode,
                  hideCaption: this.hiddenResultKpiGroups().includes(kpi.GroupCode)
                });
              }
            }
          }
        }
      }

      return retVal;
    },

    getColumnNameFromCode(code) {
      let retVal = code;

      if (this.rankingdata) {
        if (this.isCluster && this.rankingdata.Cluster) {
          const item = this.rankingdata.Cluster.find(obj => obj.Code  === code);
          if (item && item.Name) {
            retVal = item.Name;
          }
        } else {
          if (this.rankingdata.KPI) {
            const item = this.rankingdata.KPI.find(obj => {
              if (obj.Code) {
                return obj.Code === code;
              } else if (obj.GroupCode) {
                return obj.GroupCode === code;
              }
              return false;
            });
            if (item && item.Name) {
              retVal = item.Name;
            } else if (item && item.GroupName) {
              retVal = item.GroupName;
            }
          }
        }
      }

      return retVal;
    },

    getColumnDataName(index) {
      return `Columns[${index}]`;
    },

    getSelectedAggLevelName() {
      let retVal = 'N/A';

      const id = this.isLevel2Selected() ? this.aggregationValue2 : this.aggregationValue;

      if (id && id.startsWith('Property')) {
        const parent = this.aggregationItems.find(obj => obj.id === -1);
        if (parent) {
          const item = parent.children.find(obj => obj.id === id);
          if (item) {
            retVal = item.label;
          }
        }
      } else {
        const item = this.aggregationItems.find(obj => obj.id === id);
        if (item) {
          retVal = item.label;
        }
      }

      return retVal;
    },

    getGridData() {
      let retVal = null;

      if (this.rankingdata && this.rankingdata.AggregatedData) {
        retVal = [];

        const colValues = this.isCluster
          ? this.rankingdata.Cluster
          : this.rankingdata.KPI;

        const hiddenResultKpiGroups = this.hiddenResultKpiGroups();

        const mapAggregatedDataItem = (aggregatedDataItem) => {
          let resultArray = [];

          if (!aggregatedDataItem)
            return null;

          // add total value to result array
          resultArray.push({ Result: aggregatedDataItem.Result * 100 });

          for (let colValue of colValues) {
            let dataItem = undefined;

            if (this.isCluster) {
              if (aggregatedDataItem.ClusterData?.length > 0) {
                dataItem = aggregatedDataItem.ClusterData.find(item => item.Code === colValue.Code);
              }
            } else {
              if (colValue.Code) {
                if (aggregatedDataItem.KpiData?.length > 0) {
                  dataItem = aggregatedDataItem.KpiData.find(item => item.GroupCode === colValue.GroupCode && item.Code === colValue.Code);
                }
              } else {
                if (aggregatedDataItem.KpiData?.length > 0) {
                  dataItem = aggregatedDataItem.KpiData.find(item => item.GroupCode === colValue.GroupCode && !item.Code);

                  if (hiddenResultKpiGroups.includes(dataItem.GroupCode)) {
                    delete dataItem.Result;
                  }
                }
              }
            }

            // add cluster/kpi value to result array
            resultArray.push(
              {
                Code: colValue.Code,
                GroupCode: colValue.GroupCode,
                Result: dataItem?.Result * 100
              });
          }

          return resultArray;
        };

        retVal.push(...this.rankingdata.AggregatedData
          .filter(ad => ad.RankingOrder != undefined)
          .map(ad => {
            let parentItem = this.rankingdata.AggregatedData.find(rd =>
                    !rd.RankingOrder &&
                    (!this.isLevel2Selected() || rd.AggregationLevel1 === ad.AggregationLevel1));

            return {
              RankingOrder: ad.RankingOrder,
              GroupValue: this.isLevel2Selected() ?
                this.getAggLevelName(ad.AggregationLevel1Value) + (this.aggregationValue == "Location" && ad.ClientLocationIdOnClientSite ? ` (${ad.ClientLocationIdOnClientSite.trim()})` : "") :
                this.getSelectedAggLevelName(),
              AggLevelValue: this.isLevel2Selected() ?
                this.getAggLevelName(ad.AggregationLevel2Value) :
                this.getAggLevelName(ad.AggregationLevel1Value),
              SurveyInstanceCount: ad.SurveyInstanceCount,
              Columns: mapAggregatedDataItem(ad),
              RootParent: {
                SurveyInstanceCount: parentItem?.SurveyInstanceCount,
                Columns: mapAggregatedDataItem(parentItem)
              }
            };

          }));

        retVal.push(...this.rankingdata.AggregatedData
          .filter(ad => this.isLevel2Selected() && ad.AggregationLevel1Value == undefined)
          .map(ad => ({
              RankingOrder: 0,
              GroupValue: ' Total',
              AggLevelValue: 'Total',
              SurveyInstanceCount: ad.SurveyInstanceCount,
              Columns: mapAggregatedDataItem(ad),
              RootParent: {
                SurveyInstanceCount: ad.SurveyInstanceCount,
                Columns: mapAggregatedDataItem(ad)
              }
            })));
      }

      return retVal;
    },

    getAggLevelName(name) {
      return name ? name : "N/A";
    },

    onTopResultCountChange(value) {
      this.loadData();
    },

    onExpandCollapseAllChanged(checked) {
      const grid = this.$refs.dxdatagrid;

      if (grid) {
        if (checked)
          grid.$_instance.expandAll(0);
        else
          grid.$_instance.collapseAll(0);
      }
    },

    onGridRowCollapsed(event) {
      this.checkRowCollapsedValue();
    },

    onGridRowExpanded(event) {
      this.checkRowCollapsedValue();
    },

    onGridAdaptiveDetailRowPreparing(event) {
      if (event.formOptions && event.formOptions.items) {
        event.formOptions.items = event.formOptions.items.reverse();
        for (let item of event.formOptions.items) {
          if (item.column && item.name) {
            item.column.caption = this.getColumnNameFromCode(item.name);
            //console.log(item.name + ' | Prio: ' + item.column.hidingPriority);
          }
        }
      }
    },

    checkRowCollapsedValue() {
      const grid = this.$refs.dxdatagrid;
      let visibleGroupRows = grid?.$_instance.getVisibleRows()?.filter(row => row.rowType === 'group' && row.groupIndex == 0);

      if (visibleGroupRows && visibleGroupRows.length > 0) {
        let expandedStates = visibleGroupRows.map(row => grid.$_instance.isRowExpanded(row.key));

        this.expandCollapseValue = expandedStates.every(isExpanded => isExpanded);
        this.expandCollapseIndeterminate = !this.expandCollapseValue && expandedStates.some(isExpanded => isExpanded);
      }
      else {
        this.expandCollapseValue = false;
        this.expandCollapseIndeterminate = false;
      }
    },

    onToolbarPreparing(tool) {
      tool.toolbarOptions.items.unshift({
        location: "after",
        template: "download"
      });
      tool.toolbarOptions.items.unshift({
        location: "before",
        template: "collapseall"
      });
    },

    handleDataChanged() {
      this.gridData = this.getGridData();
      this.checkRowCollapsedValue();
    },

    onRowClick: function(e) {
      // console.log('ResultTable - onRowClick');
      if (e.groupIndex !== undefined && e.isExpanded !== undefined) {
        if (e.isExpanded) {
          e.component.collapseRow(e.key);
        } else {
          e.component.expandRow(e.key);
        }
      }
    },

    onCellPrepared: function(e){
      if(e.rowType == 'header') {
        e.cellElement.classList.add('header-cell-custom');
      } else {
        e.cellElement.classList.add('data-cell-custom');
      }
    },

    defaultAggregationItems: function()  {
      // console.log('RankingTable - defaultAggregationItems');
      return [ { id: 'Location', label: this.$t('Webportal.LocationId') },
               { id: 'Wave', label: this.$t('Webportal.Wave') },
               { id: 'Scenario', label: this.$t('Webportal.Scenario') } ];
    },

    downloadRawData() {
      this.isExporting = true;
      this.noDataAlert = 0;

      const options = this.getCurrentOptions();
      const filter = this.getCurrentFilter();
      const token = this.initCancelToken(this.cancelToken);

      AggregatedReportsService.getRawDataAggregated(filter, options, token)
        .then(response => {
          this.isExporting = false;

          if (response.status === 204)
            this.noDataAlert = 5;
          else
            this.downloadFile(response);
        })
        .catch(error => {
          this.isExporting = false;
          console.warn('Error to receive ranking export file: ' + error);
        });
    },

    isLevel2Selected() {
      return this.aggregationValue2 != undefined && this.aggregationValue2 != null && this.aggregationValue2 != '';
    },
  }
};
</script>

<style lang="scss" scoped>
:deep(.dx-datagrid-rowsview .dx-row.dx-group-row:not(.dx-row-focused)) {
  background-color: transparent;
}
:deep(.dx-datagrid), :deep(.dx-toolbar) {
  background-color: transparent !important;
}
:deep(.dx-datagrid-headers) {
  background-color: transparent !important;
  border-bottom: none;
}
:deep(.header-cell-custom) {
  font-weight: bold;
  color: var(--dx-datagrid-header-color);
  background-color: var(--bs-primary);
}
:deep(.data-cell-custom) {
  background-color: var(--dx-datagrid-cell-bg);
}

.excelButton {
  background-color: #107c41;
  border-color: #107c41;
  &:hover {
    color: #0e6c36;
    background-color: #fff;
    border-color: #0e6c36;
  }
}

.flex-container {
  display: flex;
  align-items: center;
}

.space-top {
  margin-top: 15px;
}

.space-left {
  margin-left: 15px;
}

.min-width {
  min-width: 175px;
}

.card-body {
  padding: 0px;
  margin-bottom: 0.5rem;
}

.dx-fieldset {
  margin: 1rem;
}

#tasks {
  min-height: 150px;
}

#tasks .dx-treelist-rowsview td {
  vertical-align: middle;
}

.pitem {
  text-align: center;
  height: 40px;
  /* width: 300px; */
  margin-left: 347px;
  margin-right: 228px;
}
</style>
